import React, { useState, forwardRef } from 'react';

import { nanoid } from '@reduxjs/toolkit';

import { bemCssModules, getTestId } from '@utils';

import InputMask, { InputMaskType } from '@components/InputMask';
import Preloader from '@components/Preloader';

import style from './InputOld.scss';

const cn = bemCssModules(style, 'InputOld');
const ti = getTestId('InputOld');

export type TInputOld = {
  className?: string;
  label?: string;
  error?: string | boolean;
  isLoading?: boolean;
  tooltipContent?: React.ReactNode;
  button?: React.ReactNode;
  testId?: string;
  maskOptions?: InputMaskType['maskOptions'];
  wrapperClassName?: string;
  withoutBorder?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'capture'>;

const InputOld = forwardRef<HTMLInputElement, TInputOld>((props, ref) => {
  const {
    className,
    label,
    disabled = false,
    error,
    isLoading = false,
    tooltipContent,
    button,
    testId,
    maskOptions,
    type = 'text',
    wrapperClassName,
    withoutBorder = false,
    ...rest
  } = props;

  const [id] = useState(nanoid());
  const isErrorMessage = error && typeof error !== 'boolean';
  const isDisabled = disabled;

  const inputProps = {
    id,
    className: cn('input', { error: !!error, noborder: withoutBorder }),
    disabled,
    'data-testid': ti('input'),
    type,
  };

  return (
    <div className={cn(null, [className])} data-testid={ti(testId)}>
      {label && (
        <label className={cn('label')} htmlFor={id}>
          {label}
        </label>
      )}
      <div
        className={cn('input-wrapper', { disabled: isDisabled, noborder: withoutBorder }, [
          wrapperClassName,
        ])}
      >
        {maskOptions ? (
          <InputMask maskOptions={maskOptions} onChange={rest.onChange} {...inputProps} {...rest} />
        ) : (
          <input ref={ref} {...inputProps} {...rest} />
        )}
        {isLoading && <Preloader className={cn('loader')} size={32} />}
      </div>
      <div className={cn('error-wrapper')}>{isErrorMessage ? error : button}</div>
    </div>
  );
});

export default InputOld;
